/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faBell,
  faEye,
  faShieldAlt,
  faGlobe,
  faPalette,
  faSave,
  faCheck,
  faTimes,
  faEdit,
  faTrash,
  faKey,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faUsers,
  faCog,
  faBuilding,
  faToggleOn,
  faToggleOff,
  faCheckCircle,
  faExclamationCircle,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import AccountDeletionModal from '../components/AccountDeletionModal';
import '../styles/UniversityDashboard.css';

const AssociationSettings = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('notifications');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    memberNotifications: true,
    newMemberAlerts: true,
    jobPostingAlerts: true,
    applicationUpdates: true,
    announcementNotifications: true,
    eventReminders: true,
    weeklyDigest: false,
    marketingEmails: false,
    pushNotifications: true,
    smsNotifications: false
  });

  // Member Management Settings
  const [memberSettings, setMemberSettings] = useState({
    autoApproveMembers: false,
    requireEmailVerification: true,
    allowMemberSelfRegistration: false,
    memberApprovalRequired: true,
    sendWelcomeEmail: true,
    notifyOnMemberJoin: true,
    notifyOnMemberUpdate: false,
    maxMembersPerUpload: 1000,
    defaultMemberRole: 'member'
  });

  // Privacy & Visibility Settings
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showContactInfo: true,
    showMemberCount: true,
    showLogoOnWebsite: true,
    searchableByMembers: true,
    allowDirectContact: true,
    showInDirectory: true
  });

  // Display Preferences
  const [displaySettings, setDisplaySettings] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD'
  });

  useEffect(() => {
    fetchAssociationSettings();
  }, []);

  const fetchAssociationSettings = async () => {
    try {
      // Don't set loading to true - render immediately with defaults
      const token = localStorage.getItem('token');
      
      // Fetch user profile for 2FA status
      try {
        const profileResponse = await axios.get(buildApiUrl('/api/user/profile'), {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (profileResponse.data) {
          const data = profileResponse.data;
          // Check for 2FA status
          if (data.twoFactorEnabled !== undefined) {
            setTwoFactorEnabled(data.twoFactorEnabled);
          }
        }
      } catch (error) {
        console.log('Profile endpoint not available');
      }

      // Fetch user settings if endpoint exists
      try {
        const settingsResponse = await axios.get(buildApiUrl('/api/user/settings'), {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (settingsResponse.data) {
          setNotificationSettings(settingsResponse.data.notifications || notificationSettings);
          setPrivacySettings(settingsResponse.data.privacy || privacySettings);
          setDisplaySettings(settingsResponse.data.display || displaySettings);
          setMemberSettings(settingsResponse.data.memberSettings || memberSettings);
        }
      } catch (error) {
        console.log('Settings endpoint not available, using defaults');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showMessage('error', 'Failed to load settings');
    }
  };

  const showMessage = (type, text) => {
    setSaveMessage({ type, text });
    setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showMessage('error', 'Password must be at least 8 characters long');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        buildApiUrl('/api/user/change-password'),
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        showMessage('success', 'Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showMessage('error', error.response?.data?.error || 'Failed to change password. Please check your current password.');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsUpdate = async (settingsType, settings) => {
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        buildApiUrl('/api/user/settings'),
        {
          [settingsType]: settings
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 200) {
        showMessage('success', 'Settings updated successfully!');
      }
    } catch (error) {
      console.error(`Error updating ${settingsType}:`, error);
      showMessage('error', `Failed to update ${settingsType} settings.`);
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (settings, setSettings, key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Standard toggle switch component (matching SettingsPage style exactly)
  const ToggleSwitch = ({ isActive, onChange, onUpdate }) => {
    const handleClick = () => {
      onChange();
      if (onUpdate) onUpdate();
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        className={isActive ? 'active' : ''}
        style={{
          fontSize: '2rem',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: isActive ? '#10b981' : '#cbd5e1',
          transition: 'all 0.3s ease',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '50px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = isActive ? '#059669' : '#94a3b8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = isActive ? '#10b981' : '#cbd5e1';
        }}
        onFocus={(e) => {
          e.currentTarget.style.outline = '2px solid #10b981';
          e.currentTarget.style.outlineOffset = '2px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = 'none';
        }}
      >
        <FontAwesomeIcon icon={isActive ? faToggleOn : faToggleOff} />
      </button>
    );
  };


  return (
    <div className="section-card" style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>
          <FontAwesomeIcon icon={faCog} style={{ marginRight: '0.5rem', color: '#f97316' }} />
          Association Settings
        </h1>
        <p style={{ color: '#64748b' }}>Manage your association account settings and preferences</p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        borderBottom: '2px solid #e5e7eb',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'notifications', label: 'Notifications', icon: faBell },
          { id: 'members', label: 'Member Management', icon: faUsers },
          { id: 'privacy', label: 'Privacy', icon: faEye },
          { id: 'security', label: 'Security', icon: faShieldAlt },
          { id: 'preferences', label: 'Preferences', icon: faPalette }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === tab.id ? '3px solid #f97316' : '3px solid transparent',
              color: activeTab === tab.id ? '#f97316' : '#64748b',
              fontWeight: activeTab === tab.id ? 600 : 500,
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <FontAwesomeIcon icon={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Success/Error Message */}
      {saveMessage.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          background: saveMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: saveMessage.type === 'success' ? '#065f46' : '#991b1b',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <FontAwesomeIcon icon={saveMessage.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {saveMessage.text}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Email Notifications
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { key: 'emailNotifications', label: 'Enable Email Notifications', desc: 'Receive notifications via email' },
                { key: 'memberNotifications', label: 'Member Activity Notifications', desc: 'Get notified when members join or update their profiles' },
                { key: 'newMemberAlerts', label: 'New Member Alerts', desc: 'Receive alerts when new members are added' },
                { key: 'jobPostingAlerts', label: 'Job Posting Alerts', desc: 'Get notified about job posting activities' },
                { key: 'applicationUpdates', label: 'Application Updates', desc: 'Receive updates on member job applications' },
                { key: 'announcementNotifications', label: 'Announcement Notifications', desc: 'Get notified when announcements are sent' },
                { key: 'eventReminders', label: 'Event Reminders', desc: 'Receive reminders for upcoming events' },
                { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Receive a weekly summary of association activity' },
                { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Receive promotional emails and updates' }
              ].map(setting => (
                <div key={setting.key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                      {setting.label}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {setting.desc}
                    </div>
                  </div>
                  <ToggleSwitch
                    isActive={notificationSettings[setting.key]}
                    onChange={() => toggleSetting(notificationSettings, setNotificationSettings, setting.key)}
                    onUpdate={() => handleSettingsUpdate('notifications', {
                      ...notificationSettings,
                      [setting.key]: !notificationSettings[setting.key]
                    })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Other Notifications
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { key: 'pushNotifications', label: 'Push Notifications', desc: 'Receive browser push notifications' },
                { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Receive notifications via SMS (requires phone number)' }
              ].map(setting => (
                <div key={setting.key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                      {setting.label}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {setting.desc}
                    </div>
                  </div>
                  <ToggleSwitch
                    isActive={notificationSettings[setting.key]}
                    onChange={() => toggleSetting(notificationSettings, setNotificationSettings, setting.key)}
                    onUpdate={() => handleSettingsUpdate('notifications', {
                      ...notificationSettings,
                      [setting.key]: !notificationSettings[setting.key]
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Member Management Tab */}
      {activeTab === 'members' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Member Approval Settings
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { key: 'autoApproveMembers', label: 'Auto-Approve New Members', desc: 'Automatically approve members when they join' },
                { key: 'requireEmailVerification', label: 'Require Email Verification', desc: 'Members must verify their email before joining' },
                { key: 'allowMemberSelfRegistration', label: 'Allow Self-Registration', desc: 'Allow members to register themselves' },
                { key: 'memberApprovalRequired', label: 'Manual Approval Required', desc: 'Require manual approval for all new members' },
                { key: 'sendWelcomeEmail', label: 'Send Welcome Email', desc: 'Automatically send welcome email to new members' },
                { key: 'notifyOnMemberJoin', label: 'Notify on Member Join', desc: 'Get notified when a new member joins' },
                { key: 'notifyOnMemberUpdate', label: 'Notify on Member Update', desc: 'Get notified when members update their profiles' }
              ].map(setting => (
                <div key={setting.key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                      {setting.label}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {setting.desc}
                    </div>
                  </div>
                  <ToggleSwitch
                    isActive={memberSettings[setting.key]}
                    onChange={() => toggleSetting(memberSettings, setMemberSettings, setting.key)}
                    onUpdate={() => handleSettingsUpdate('memberSettings', {
                      ...memberSettings,
                      [setting.key]: !memberSettings[setting.key]
                    })}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Bulk Upload Settings
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Max Members Per Upload
                </label>
                <input
                  type="number"
                  value={memberSettings.maxMembersPerUpload}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 1000;
                    setMemberSettings({ ...memberSettings, maxMembersPerUpload: newValue });
                    handleSettingsUpdate('memberSettings', {
                      ...memberSettings,
                      maxMembersPerUpload: newValue
                    });
                  }}
                  min="1"
                  max="10000"
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Default Member Role
                </label>
                <select
                  value={memberSettings.defaultMemberRole}
                  onChange={(e) => {
                    setMemberSettings({ ...memberSettings, defaultMemberRole: e.target.value });
                    handleSettingsUpdate('memberSettings', {
                      ...memberSettings,
                      defaultMemberRole: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="member">Member</option>
                  <option value="premium">Premium Member</option>
                  <option value="vip">VIP Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Tab */}
      {activeTab === 'privacy' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Profile Visibility
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Profile Visibility
                </label>
                <select
                  value={privacySettings.profileVisibility}
                  onChange={(e) => {
                    setPrivacySettings({ ...privacySettings, profileVisibility: e.target.value });
                    handleSettingsUpdate('privacy', {
                      ...privacySettings,
                      profileVisibility: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="public">Public - Visible to everyone</option>
                  <option value="members">Members Only - Visible to association members</option>
                  <option value="private">Private - Only visible to you</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Information Display
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { key: 'showContactInfo', label: 'Show Contact Information', desc: 'Display contact details on public profile' },
                { key: 'showMemberCount', label: 'Show Member Count', desc: 'Display total member count publicly' },
                { key: 'showLogoOnWebsite', label: 'Show Logo on Website', desc: 'Display association logo on public website' },
                { key: 'searchableByMembers', label: 'Searchable by Members', desc: 'Allow members to search for your association' },
                { key: 'allowDirectContact', label: 'Allow Direct Contact', desc: 'Allow members to contact you directly' },
                { key: 'showInDirectory', label: 'Show in Directory', desc: 'Include association in public directory' }
              ].map(setting => (
                <div key={setting.key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                      {setting.label}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {setting.desc}
                    </div>
                  </div>
                  <ToggleSwitch
                    isActive={privacySettings[setting.key]}
                    onChange={() => toggleSetting(privacySettings, setPrivacySettings, setting.key)}
                    onUpdate={() => handleSettingsUpdate('privacy', {
                      ...privacySettings,
                      [setting.key]: !privacySettings[setting.key]
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div style={{ display: 'grid', gap: '2rem' }}>
          <form onSubmit={handlePasswordChange}>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
                Change Password
              </h3>
              <div style={{ display: 'grid', gap: '1rem', maxWidth: '500px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                    Current Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                    New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={8}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                    Must be at least 8 characters long
                  </p>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: 'auto',
                    alignSelf: 'flex-start'
                  }}
                >
                  {saving ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </form>

          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Two-Factor Authentication
            </h3>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div>
                <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>
                  Enable Two-Factor Authentication
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  Add an extra layer of security to your account
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setTwoFactorEnabled(!twoFactorEnabled);
                  // TODO: Implement 2FA enable/disable API call
                  showMessage('info', '2FA feature coming soon');
                }}
                style={{
                  background: twoFactorEnabled ? '#10b981' : '#d1d5db',
                  border: 'none',
                  borderRadius: '9999px',
                  width: '48px',
                  height: '24px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: '2px',
                  left: twoFactorEnabled ? '26px' : '2px',
                  width: '20px',
                  height: '20px',
                  background: 'white',
                  borderRadius: '50%',
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>
          </div>

          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#ef4444' }}>
              Danger Zone
            </h3>
            <div style={{
              padding: '1.5rem',
              background: '#fef2f2',
              borderRadius: '8px',
              border: '2px solid #fecaca'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '0.5rem' }}>
                  Delete Association Account
                </div>
                <div style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '1rem' }}>
                  Permanently delete your association account and all associated data. This action cannot be undone.
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  style={{
                    padding: '10px 20px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          <div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#0f172a' }}>
              Display Preferences
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Theme
                </label>
                <select
                  value={displaySettings.theme}
                  onChange={(e) => {
                    setDisplaySettings({ ...displaySettings, theme: e.target.value });
                    handleSettingsUpdate('display', {
                      ...displaySettings,
                      theme: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto (System)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Language
                </label>
                <select
                  value={displaySettings.language}
                  onChange={(e) => {
                    setDisplaySettings({ ...displaySettings, language: e.target.value });
                    handleSettingsUpdate('display', {
                      ...displaySettings,
                      language: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Timezone
                </label>
                <select
                  value={displaySettings.timezone}
                  onChange={(e) => {
                    setDisplaySettings({ ...displaySettings, timezone: e.target.value });
                    handleSettingsUpdate('display', {
                      ...displaySettings,
                      timezone: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Kolkata">Mumbai (IST)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Africa/Nairobi">Nairobi (EAT)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Date Format
                </label>
                <select
                  value={displaySettings.dateFormat}
                  onChange={(e) => {
                    setDisplaySettings({ ...displaySettings, dateFormat: e.target.value });
                    handleSettingsUpdate('display', {
                      ...displaySettings,
                      dateFormat: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#0f172a' }}>
                  Currency
                </label>
                <select
                  value={displaySettings.currency}
                  onChange={(e) => {
                    setDisplaySettings({ ...displaySettings, currency: e.target.value });
                    handleSettingsUpdate('display', {
                      ...displaySettings,
                      currency: e.target.value
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '300px',
                    padding: '10px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="KES">KES (KSh)</option>
                  <option value="ZAR">ZAR (R)</option>
                  <option value="NGN">NGN (₦)</option>
                  <option value="INR">INR (₹)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Deletion Modal */}
      {showDeleteModal && (
        <AccountDeletionModal
          onClose={() => setShowDeleteModal(false)}
          onDelete={async () => {
            try {
              const token = localStorage.getItem('token');
              await axios.delete(buildApiUrl('/api/user/delete-account'), {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (logout) logout();
              navigate('/');
              alert('Your association account has been permanently deleted.');
            } catch (error) {
              console.error('Error deleting account:', error);
              showMessage('error', 'Failed to delete account. Please try again.');
            }
          }}
        />
      )}
    </div>
  );
};

export default AssociationSettings;

